import React, { useCallback, useState, useEffect, useRef } from "react";
import MidasActionCard from "./MidasActionCard";
import type { ChatActionRequest } from "../models/chatBotModels";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import useActionExecutor from "../actions/useActionExecutor";
import type { ActionResult } from "../actions/actionRegistry";
import { STALE_ACTION_TIMEOUT_MS } from "../actions/actionRegistry";
import { getHardcodedSchema } from "../actions/actionDefinitions";
import { openArtifact, closeArtifactTab } from "../../../customer/midas/midasPageSlice";
// Side-effect import: registers action definitions
import "../actions/actionDefinitions";

// ============================================================
// MidasActionCardWrapper — bridges ChatBotMessage and the
// action execution system. Opens the artifact pane with full
// action form and shows a compact summary card in chat.
// ============================================================

interface MidasActionCardWrapperProps {
  actionRequest: ChatActionRequest;
}

const MidasActionCardWrapper: React.FC<MidasActionCardWrapperProps> = ({
  actionRequest,
}) => {
  const dispatch = useAppDispatch();
  const { executeAction, cancelAction, canExecute, isStale } = useActionExecutor();
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);
  const activeThreadId = useAppSelector((s) => s.midasPage?.activeThreadId);
  const artifactTabs = useAppSelector((s) => s.midasPage?.artifactTabs);

  // Local state for the card (simpler than full Redux round-trip)
  const [cardState, setCardState] = useState<
    "pending" | "executing" | "success" | "error" | "stale" | "denied"
  >("pending");
  const [result, setResult] = useState<ActionResult | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [retryCount, setRetryCount] = useState(0);
  const artifactTabIdRef = useRef<string | null>(null);

  // Derive module from action_type prefix (e.g., "machinery.create_service_report" -> "Machinery")
  const moduleFromType = actionRequest.action_type.split(".")[0];
  const moduleName = moduleFromType
    ? moduleFromType.charAt(0).toUpperCase() + moduleFromType.slice(1)
    : "Machinery";

  // Check permission on mount
  const { allowed, reason } = canExecute(
    actionRequest.action_type,
    moduleName
  );

  // Set initial state based on permission
  useEffect(() => {
    if (!allowed && reason === "permission_denied") {
      setCardState("denied");
    }
  }, [allowed, reason]);

  // Stale check -- poll every 30 seconds
  useEffect(() => {
    const createdAt = Date.now();
    const interval = setInterval(() => {
      if (isStale(createdAt) && cardState === "pending") {
        setCardState("stale");
      }
    }, 30_000);
    return () => clearInterval(interval);
  }, [isStale, cardState]);

  // Track the artifact tab ID so we can close it on cancel
  useEffect(() => {
    if (artifactTabs) {
      const tab = artifactTabs.find(
        (t) => t.artifact.type === "action_form" && t.artifact.title === actionRequest.display.title
      );
      if (tab) {
        artifactTabIdRef.current = tab.id;
      }
    }
  }, [artifactTabs, actionRequest.display.title]);

  const handleConfirm = useCallback(
    async (values: Record<string, any>) => {
      setCardState("executing");
      setErrorMessage(undefined);

      // MA-0b.3: Pass machineId and threadId so result can be reported back to bot
      const actionResult = await executeAction(
        actionRequest,
        values,
        machineContext?.machineId,
        activeThreadId || undefined
      );

      if (actionResult.success) {
        setCardState("success");
        setResult(actionResult);
      } else {
        setCardState("error");
        setErrorMessage(actionResult.message);
      }

      // Close the artifact tab after execution
      if (artifactTabIdRef.current) {
        dispatch(closeArtifactTab(artifactTabIdRef.current));
      }
    },
    [executeAction, actionRequest, machineContext, activeThreadId, dispatch]
  );

  const handleCancel = useCallback(() => {
    cancelAction(
      actionRequest,
      machineContext?.machineId,
      activeThreadId || undefined
    );
    // Gray out the card after cancel
    setCardState("stale");

    // Close the artifact tab
    if (artifactTabIdRef.current) {
      dispatch(closeArtifactTab(artifactTabIdRef.current));
    }
  }, [cancelAction, actionRequest, machineContext, activeThreadId, dispatch]);

  const handleRetry = useCallback(() => {
    // Max 2 auto-retries
    if (retryCount >= 2) return;
    setRetryCount((c) => c + 1);
    setCardState("pending");
    setErrorMessage(undefined);
  }, [retryCount]);

  const handleDismiss = useCallback(() => {
    setCardState("stale");
  }, []);

  // Open the action form in the artifact pane
  const handleOpenInPanel = useCallback(() => {
    const schema = getHardcodedSchema(actionRequest.action_type);
    const fields = schema?.fields || actionRequest.schema?.fields || [];

    dispatch(
      openArtifact({
        type: "action_form",
        title: actionRequest.display.title,
        source: "machineid",
        url: "",
        data: {
          fields,
          values: actionRequest.values,
          actionRequest,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
        },
      })
    );
  }, [actionRequest, dispatch, handleConfirm, handleCancel]);

  // Auto-open the artifact pane when the card first renders in pending state
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (cardState === "pending" && allowed && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      handleOpenInPanel();
    }
  }, [cardState, allowed, handleOpenInPanel]);

  return (
    <MidasActionCard
      actionRequest={actionRequest}
      state={cardState}
      result={result}
      errorMessage={errorMessage}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      onRetry={retryCount < 2 ? handleRetry : undefined}
      onDismiss={handleDismiss}
      permissionDenied={cardState === "denied"}
      onOpenInPanel={handleOpenInPanel}
    />
  );
};

export default MidasActionCardWrapper;
